import { useState, useEffect } from 'react';

export interface AdminAuthState {
  isAdminAuthenticated: boolean;
  refreshAdminAuth: () => void;
}

/**
 * Hook to detect if admin authentication is active from within the main app.
 * 
 * This checks for admin authentication by:
 * 1. Looking for admin access token in memory (if available)
 * 2. Making a request to admin auth status endpoint
 */
export function useAdminAuthDetection(): AdminAuthState {
  const [isAdminAuthenticated, setIsAdminAuthenticated] = useState(false);

  const checkAdminAuth = async (includeHttpCheck = false) => {
    try {
      // Primary method: Check for admin access token in global scope
      // This is fast and doesn't hit any rate limits
      const adminToken = (window as any).__adminAccessToken__;
      const hasAdminToken = adminToken && typeof adminToken === 'string' && adminToken.length > 0;

      if (hasAdminToken) {
        setIsAdminAuthenticated(true);
        return;
      }

      // Secondary method: HTTP check (only when explicitly requested)
      // This respects rate limits by not being called automatically
      if (includeHttpCheck) {
        try {
          const response = await fetch('/admin/auth/status', {
            method: 'GET',
            credentials: 'include',
          });

          if (response.ok) {
            const data = await response.json();
            const hasAdminSession = data.authenticated === true;
            setIsAdminAuthenticated(hasAdminSession);
            return;
          }
        } catch (error) {
          // If HTTP request fails, fall through to false
        }
      }

      // No admin auth detected
      setIsAdminAuthenticated(false);
      
    } catch (error) {
      setIsAdminAuthenticated(false);
    }
  };

  const refreshAdminAuth = () => {
    // When manually refreshed, include HTTP check
    checkAdminAuth(true);
  };

  useEffect(() => {
    // Initial check - try both methods since we're likely in different tabs
    // The global token method works for same-tab, HTTP method works cross-tab
    const initialCheck = async () => {
      // First try the fast global token check
      await checkAdminAuth(false);
      
      // If no global token found, try HTTP check as fallback (for cross-tab detection)
      if (!(window as any).__adminAccessToken__) {
        await checkAdminAuth(true);
      }
    };
    
    initialCheck();
  }, []);

  return {
    isAdminAuthenticated,
    refreshAdminAuth,
  };
}
